import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import type { ApiError } from '../api';
import { organizationEndpoints } from '../api';
import { LEAD_TONE, groupBookingEndpoints } from '../api-group-bookings';
import type { Board, Booking, GroupProduct, LeadDetail, LeadStatus, Lookups, PlanInput, Priced, Quote } from '../api-group-bookings';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import { INPUT, dollars } from './event/shared';

/**
 * One group lead (D-376): who they are, where they are in the pipeline, and
 * everything that can happen next — a note or a call, a quote (versioned and
 * emailed), a hold on a space for a game, a booking into an order with a
 * payment link, an invoice or a house account charge. The timeline under it is
 * append-only: what was done, by whom, when.
 */

const ACTIVITY: Record<string, string> = {
  INQUIRY: 'Inquiry',
  NOTE: 'Note',
  CALL: 'Call',
  STATUS_CHANGED: 'Status',
  ASSIGNED: 'Owner',
  FOLLOW_UP_SET: 'Follow-up',
  DETAILS_CHANGED: 'Details',
  EMAIL_SENT: 'Email',
  QUOTE_CREATED: 'Quote',
  QUOTE_SENT: 'Quote sent',
  QUOTE_ACCEPTED: 'Accepted',
  HOLD_PLACED: 'Hold',
  HOLD_RELEASED: 'Released',
  BOOKED: 'Booked',
  BOOKING_CANCELLED: 'Canceled',
  PAID: 'Paid',
};

const BOOKING_TONE: Record<Booking['status'], 'ok' | 'warn' | 'neutral'> = { HELD: 'warn', BOOKED: 'ok', RELEASED: 'neutral', CANCELLED: 'neutral' };

export default function GroupLeadPage() {
  const { id = '' } = useParams();
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; url?: string } | null>(null);
  const detail = useQuery<LeadDetail, ApiError>({ queryKey: ['group-lead', id], queryFn: () => groupBookingEndpoints.lead(id) });
  const lookups = useQuery<Lookups, ApiError>({ queryKey: ['group-lookups'], queryFn: groupBookingEndpoints.lookups });
  const products = useQuery({ queryKey: ['group-products'], queryFn: groupBookingEndpoints.products });
  const board = useQuery<Board, ApiError>({ queryKey: ['group-board', 'upcoming'], queryFn: () => groupBookingEndpoints.board() });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['group-lead', id] });
    void queryClient.invalidateQueries({ queryKey: ['group-leads'] });
    void queryClient.invalidateQueries({ queryKey: ['group-board'] });
  };
  const report = { onError: (cause: ApiError) => setError(cause.message), refresh, notify: (text: string, url?: string) => setNotice(url ? { text, url } : { text }) };

  if (!can('group_sales.view')) return <NotPermitted permission="group_sales.view" />;
  if (detail.error) return <ErrorNote>{detail.error.message}</ErrorNote>;
  if (!detail.data || !lookups.data) return <p className="text-sm text-ink-500">Loading…</p>;

  const { lead, activities, quotes, bookings, games, emails, organization } = detail.data;
  const statusName = lookups.data.statuses.find((s) => s.code === lead.status)?.name ?? lead.status;
  const allProducts = products.data?.products ?? [];
  const interest = allProducts.find((p) => p.id === lead.interest_product_id);
  const manage = can('group_sales.manage');
  const open = !['COMPLETED', 'CANCELLED'].includes(lead.status);

  return (
    <div className="space-y-4">
      <p className="text-sm">
        <Link to="/group-sales" className="text-live hover:underline">
          ← Group sales
        </Link>
      </p>
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {notice ? (
        <div className="space-y-2 rounded-lg border border-line bg-surface px-5 py-3 text-sm" role="status">
          <p>{notice.text}</p>
          {notice.url ? (
            <div className="flex gap-2">
              <input readOnly value={notice.url} onFocus={(e) => e.currentTarget.select()} className={`w-full font-mono text-xs ${INPUT}`} aria-label="Link" />
              <Button onClick={() => void navigator.clipboard?.writeText(notice.url!)}>Copy</Button>
            </div>
          ) : null}
          <Button onClick={() => setNotice(null)}>Done</Button>
        </div>
      ) : null}

      <Panel
        title={`${lead.contact_name}${lead.organization_name ? ` · ${lead.organization_name}` : ''}`}
        description={`${lead.reference} · ${lookups.data.sources.find((s) => s.code === lead.source)?.name ?? lead.source} · came in ${formatWhen(lead.created_at)}`}
        actions={<Badge tone={LEAD_TONE[lead.status]}>{statusName}</Badge>}
      >
        <div className="grid gap-6 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <Fact label="Contact">
            {lead.contact_email ? <a className="text-live hover:underline" href={`mailto:${lead.contact_email}`}>{lead.contact_email}</a> : null}
            {lead.contact_phone ? <div>{lead.contact_phone}</div> : null}
            {lead.customer_id ? (
              <Link to={`/customers/${lead.customer_id}`} className="text-xs text-live hover:underline">
                Customer record
              </Link>
            ) : null}
          </Fact>
          <Fact label="Group">
            {lookups.data.group_types.find((t) => t.code === lead.group_type)?.name ?? '—'}
            {lead.headcount ? <div className="text-xs text-ink-500">{lead.headcount} people</div> : null}
            {organization ? (
              <Link to={`/organizations?id=${organization.id}`} className="text-xs text-live hover:underline">
                Organization record
              </Link>
            ) : null}
          </Fact>
          <Fact label="Wants">
            {interest?.name ?? 'Not sure yet'}
            <div className="text-xs text-ink-500">
              {games.map((g) => `${g.event_title} · ${g.local_event_date}`).join('; ') || (lead.flexible_dates ? 'Any game' : '')}
              {lead.preferred_dates_text ? ` ${lead.preferred_dates_text}` : ''}
            </div>
            {lead.budget_text ? <div className="text-xs text-ink-500">Budget: {lead.budget_text}</div> : null}
          </Fact>
          <Fact label="Owner · follow up">
            {lead.owner_name ?? 'Nobody'}
            <div className="text-xs text-ink-500">{lead.next_follow_up_on ? `Follow up ${lead.next_follow_up_on}` : 'No follow-up set'}</div>
            {lead.close_reason ? <div className="text-xs text-ink-500">{lead.close_reason}</div> : null}
          </Fact>
        </div>
        {lead.message ? <p className="border-t border-line px-5 py-3 text-sm whitespace-pre-line text-ink-600">{lead.message}</p> : null}
      </Panel>

      {manage ? <WorkPanel detail={detail.data} lookups={lookups.data} {...report} /> : null}

      {can('group_sales.quote') && open ? <QuoteBuilder leadId={id} products={allProducts} board={board.data ?? null} lead={detail.data} {...report} /> : null}

      <QuotesPanel quotes={quotes} leadId={id} canQuote={can('group_sales.quote')} canBook={can('group_sales.book') && open} {...report} />

      <BookingsPanel bookings={bookings} leadId={id} canBook={can('group_sales.book')} houseAccount={Boolean(organization?.house_account_enabled)} {...report} />

      <Panel title="Timeline" description="Everything that happened to this lead. Nothing here can be edited.">
        <ol className="divide-y divide-line">
          {activities.map((a) => (
            <li key={a.id} className="flex flex-wrap gap-3 px-5 py-2.5 text-sm">
              <span className="w-40 shrink-0 text-xs text-ink-500">{formatWhen(a.created_at)}</span>
              <span className="w-24 shrink-0">
                <Badge>{ACTIVITY[a.kind] ?? a.kind}</Badge>
              </span>
              <span className="min-w-0 flex-1 whitespace-pre-line">
                {a.from_status || a.to_status ? `${a.from_status ?? '—'} → ${a.to_status ?? '—'}${a.body ? ': ' : ''}` : ''}
                {a.body}
              </span>
              <span className="text-xs text-ink-500">{a.staff_name ?? (a.actor_type === 'CUSTOMER' ? 'The contact' : 'Automatic')}</span>
            </li>
          ))}
        </ol>
      </Panel>

      <Panel title="Emails" description="What was sent about this lead.">
        <Table headers={['Email', 'To', 'Status', 'When']}>
          {emails.map((email) => (
            <tr key={email.id} className="border-b border-line last:border-0">
              <td className="px-5 py-2 text-sm">{email.subject_snapshot ?? email.template_code.toLowerCase().replaceAll('_', ' ')}</td>
              <td className="px-5 py-2 text-sm">{email.to_email}</td>
              <td className="px-5 py-2 text-sm">
                {email.status.toLowerCase()}
                {email.last_error ? <div className="text-xs text-bad">{email.last_error}</div> : null}
              </td>
              <td className="px-5 py-2 text-sm">{formatWhen(email.sent_at ?? email.requested_at)}</td>
            </tr>
          ))}
        </Table>
      </Panel>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="text-sm">
      <div className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

interface Report {
  onError: (message: ApiError) => void;
  refresh: () => void;
  notify: (text: string, url?: string) => void;
}

/** Notes and calls, the status, the owner and follow-up, and the organization. */
function WorkPanel({ detail, lookups, onError, refresh, notify }: { detail: LeadDetail; lookups: Lookups } & Report) {
  const { lead } = detail;
  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const [kind, setKind] = useState<'NOTE' | 'CALL'>('CALL');
  const [followUp, setFollowUp] = useState(lead.next_follow_up_on ?? '');
  const [status, setStatus] = useState<LeadStatus | ''>('');
  const [reason, setReason] = useState('');
  const [owner, setOwner] = useState(lead.owner_staff_account_id ?? '');
  const [orgId, setOrgId] = useState(lead.organization_id ?? '');
  const organizations = useQuery({ queryKey: ['organizations'], queryFn: organizationEndpoints.list });
  useEffect(() => {
    setFollowUp(lead.next_follow_up_on ?? '');
    setOwner(lead.owner_staff_account_id ?? '');
    setOrgId(lead.organization_id ?? '');
  }, [lead.next_follow_up_on, lead.owner_staff_account_id, lead.organization_id]);
  const moves = lookups.transitions.filter((t) => t.from_status === lead.status).map((t) => t.to_status);
  const needsReason = lookups.statuses.find((s) => s.code === status)?.requires_reason === 1;
  const done = { onError, onSuccess: () => refresh() };

  const addNote = useMutation({ mutationFn: () => groupBookingEndpoints.note(lead.id, { kind, body: note.trim(), next_follow_up_on: followUp || null }), ...done, onSuccess: () => { setNote(''); refresh(); } });
  const move = useMutation({ mutationFn: () => groupBookingEndpoints.status(lead.id, status as LeadStatus, reason.trim() || null), ...done, onSuccess: () => { setStatus(''); setReason(''); refresh(); } });
  const save = useMutation({ mutationFn: () => groupBookingEndpoints.updateLead(lead.id, { owner_staff_account_id: owner || null, next_follow_up_on: followUp || null, organization_id: orgId || null }), ...done });
  const saveOrg = useMutation({ mutationFn: () => groupBookingEndpoints.saveOrganization(lead.id), ...done });
  const rebook = useMutation({ mutationFn: () => groupBookingEndpoints.rebook(lead.id), onError, onSuccess: (r) => { notify(`A new lead was made for ${lead.contact_name}: ${r.lead.reference}.`); navigate(`/group-sales/leads/${r.lead.id}`); } });

  return (
    <Panel title="Work this lead">
      <div className="grid gap-5 px-5 py-4 lg:grid-cols-2">
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            addNote.mutate();
          }}
        >
          <div className="flex gap-3 text-sm">
            <label className="flex items-center gap-1"><input type="radio" checked={kind === 'CALL'} onChange={() => setKind('CALL')} /> Call</label>
            <label className="flex items-center gap-1"><input type="radio" checked={kind === 'NOTE'} onChange={() => setKind('NOTE')} /> Note</label>
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={kind === 'CALL' ? 'What was said on the call' : 'A note for whoever picks this up'} aria-label="Note" className={`w-full ${INPUT}`} />
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Next follow-up" htmlFor="lead-follow">
              <input id="lead-follow" type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className={INPUT} />
            </Field>
            <Button type="submit" variant="primary" disabled={addNote.isPending || !note.trim()}>
              {kind === 'CALL' ? 'Log the call' : 'Add the note'}
            </Button>
          </div>
        </form>

        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Owner" htmlFor="lead-owner">
              <select id="lead-owner" value={owner} onChange={(e) => setOwner(e.target.value)} className={INPUT}>
                <option value="">Nobody</option>
                {lookups.owners.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.display_name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Organization" htmlFor="lead-org">
              <select id="lead-org" value={orgId} onChange={(e) => setOrgId(e.target.value)} className={`w-56 ${INPUT}`}>
                <option value="">Not linked</option>
                {(organizations.data?.organizations ?? []).map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </Field>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              Save
            </Button>
            {!lead.organization_id && lead.organization_name ? (
              <Button onClick={() => saveOrg.mutate()} disabled={saveOrg.isPending}>
                Keep {lead.organization_name} as an organization
              </Button>
            ) : null}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Move to" htmlFor="lead-status">
              <select id="lead-status" value={status} onChange={(e) => setStatus(e.target.value as LeadStatus)} className={INPUT}>
                <option value="">—</option>
                {moves.map((m) => (
                  <option key={m} value={m}>
                    {lookups.statuses.find((s) => s.code === m)?.name ?? m}
                  </option>
                ))}
              </select>
            </Field>
            {needsReason ? (
              <Field label="Why" htmlFor="lead-reason">
                <input id="lead-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={`w-64 ${INPUT}`} />
              </Field>
            ) : null}
            <Button onClick={() => move.mutate()} disabled={!status || move.isPending || (needsReason && !reason.trim())}>
              Move
            </Button>
            {['BOOKED', 'COMPLETED'].includes(lead.status) ? (
              <Button onClick={() => rebook.mutate()} disabled={rebook.isPending}>
                Rebook for another game
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-ink-500">On hold and Booked follow from holding and booking a space below, not from this list.</p>
        </div>
      </div>
    </Panel>
  );
}

const plus = (ymd: string, days: number) => {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/** Price a space, package or block for a game, see the total as it is built, and send it. */
function QuoteBuilder({ leadId, products, board, lead, onError, refresh, notify }: { leadId: string; products: GroupProduct[]; board: Board | null; lead: LeadDetail } & Report) {
  const today = new Date().toISOString().slice(0, 10);
  const [plan, setPlan] = useState<PlanInput>({
    performance_id: lead.games[0]?.performance_id ?? '',
    product_id: lead.lead.interest_product_id ?? '',
    option_product_ids: [],
    extra_tickets: 0,
    discount_cents: 0,
    discount_reason: null,
    headcount: lead.lead.headcount,
  });
  const [terms, setTerms] = useState({ deposit: '', deposit_due_on: plus(today, 7), balance_due_on: plus(today, 14), valid_until: plus(today, 7), message: '', send: Boolean(lead.lead.contact_email) });
  const [priced, setPriced] = useState<Priced | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);
  const product = products.find((p) => p.id === plan.product_id);
  const game = board?.games.find((g) => g.performance_id === plan.performance_id);
  const cell = game?.spaces.find((s) => s.product_id === plan.product_id);

  useEffect(() => {
    if (!plan.performance_id || !plan.product_id) {
      setPriced(null);
      return;
    }
    let live = true;
    const timer = setTimeout(() => {
      groupBookingEndpoints
        .price(plan)
        .then((result) => {
          if (!live) return;
          setPriced(result);
          setPriceError(null);
        })
        .catch((cause: ApiError) => {
          if (!live) return;
          setPriced(null);
          setPriceError(cause.message);
        });
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [plan]);

  const create = useMutation({
    mutationFn: () =>
      groupBookingEndpoints.quote(leadId, {
        ...plan,
        deposit_cents: Math.round(Number(terms.deposit || 0) * 100),
        deposit_due_on: terms.deposit ? terms.deposit_due_on : null,
        balance_due_on: terms.balance_due_on || null,
        valid_until: terms.valid_until || null,
        message: terms.message.trim() || null,
        send: terms.send,
      }),
    onError,
    onSuccess: (result) => {
      notify(`Quote version ${result.quote.version} ${terms.send ? 'emailed' : 'saved'}: ${dollars(result.quote.total_cents)}.`, result.url);
      refresh();
    },
  });

  return (
    <Panel title="Quote" description="Pick a game and what they want; the total is priced as the order will be.">
      <div className="grid gap-3 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Game" htmlFor="q-game">
          <select id="q-game" value={plan.performance_id} onChange={(e) => setPlan({ ...plan, performance_id: e.target.value })} className={`w-full ${INPUT}`}>
            <option value="">Pick a game</option>
            {(board?.games ?? []).map((g) => (
              <option key={g.performance_id} value={g.performance_id}>
                {g.local_event_date} · {g.event_title}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Space or package" htmlFor="q-product" hint={cell ? (cell.open > 0 ? 'Open that game' : 'Taken that game') : undefined}>
          <select id="q-product" value={plan.product_id} onChange={(e) => setPlan({ ...plan, product_id: e.target.value, option_product_ids: [] })} className={`w-full ${INPUT}`}>
            <option value="">Pick one</option>
            {products.filter((p) => p.active === 1).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={product?.kind === 'TICKET_BLOCK' ? `Tickets${product.min_guests ? ` (${product.min_guests}+)` : ''}` : 'Extra tickets'} htmlFor="q-extra" hint={product && product.included_tickets > 0 ? `${product.included_tickets} included` : undefined}>
          <input id="q-extra" inputMode="numeric" value={plan.extra_tickets || ''} onChange={(e) => setPlan({ ...plan, extra_tickets: Number(e.target.value.replace(/\D/g, '') || 0) })} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Headcount" htmlFor="q-head">
          <input id="q-head" inputMode="numeric" value={plan.headcount ?? ''} onChange={(e) => setPlan({ ...plan, headcount: e.target.value ? Number(e.target.value.replace(/\D/g, '')) : null })} className={`w-full ${INPUT}`} />
        </Field>
        {product && product.options.length > 0 ? (
          <div className="sm:col-span-2 lg:col-span-4 flex flex-wrap gap-4 text-sm">
            {product.options.map((option) => (
              <label key={option.option_product_id} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={plan.option_product_ids.includes(option.option_product_id)}
                  onChange={(e) => setPlan({ ...plan, option_product_ids: e.target.checked ? [...plan.option_product_ids, option.option_product_id] : plan.option_product_ids.filter((x) => x !== option.option_product_id) })}
                />
                Add {option.name} (+{dollars(option.price_cents)})
              </label>
            ))}
          </div>
        ) : null}
        <Field label="Discount ($)" htmlFor="q-discount">
          <input id="q-discount" inputMode="decimal" value={plan.discount_cents ? String(plan.discount_cents / 100) : ''} onChange={(e) => setPlan({ ...plan, discount_cents: Math.round(Number(e.target.value.replace(/[^0-9.]/g, '') || 0) * 100) })} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Why the discount" htmlFor="q-reason">
          <input id="q-reason" value={plan.discount_reason ?? ''} onChange={(e) => setPlan({ ...plan, discount_reason: e.target.value || null })} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Deposit ($)" htmlFor="q-deposit">
          <input id="q-deposit" inputMode="decimal" value={terms.deposit} onChange={(e) => setTerms({ ...terms, deposit: e.target.value.replace(/[^0-9.]/g, '') })} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Deposit due" htmlFor="q-deposit-due">
          <input id="q-deposit-due" type="date" value={terms.deposit_due_on} onChange={(e) => setTerms({ ...terms, deposit_due_on: e.target.value })} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Balance due" htmlFor="q-balance-due">
          <input id="q-balance-due" type="date" value={terms.balance_due_on} onChange={(e) => setTerms({ ...terms, balance_due_on: e.target.value })} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Good until" htmlFor="q-valid">
          <input id="q-valid" type="date" value={terms.valid_until} onChange={(e) => setTerms({ ...terms, valid_until: e.target.value })} className={`w-full ${INPUT}`} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Message on the quote" htmlFor="q-message">
            <textarea id="q-message" rows={2} value={terms.message} onChange={(e) => setTerms({ ...terms, message: e.target.value })} className={`w-full ${INPUT}`} />
          </Field>
        </div>
        <div className="sm:col-span-2 lg:col-span-4 rounded border border-line bg-page p-3 text-sm">
          {priceError ? <p className="text-bad">{priceError}</p> : null}
          {priced ? (
            <>
              <ul className="space-y-1">
                {priced.lines.map((line, index) => (
                  <li key={index} className="flex justify-between gap-3">
                    <span>{line.description}</span>
                    <span className="tabular">{dollars(line.amount_cents)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 flex justify-between border-t border-line pt-2 font-semibold">
                <span>Total · {priced.ticket_count} tickets</span>
                <span className="tabular">{dollars(priced.total_cents)}</span>
              </p>
            </>
          ) : !priceError ? (
            <p className="text-ink-500">Pick a game and a space to see the price.</p>
          ) : null}
        </div>
        <div className="sm:col-span-2 lg:col-span-4 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1 text-sm">
            <input type="checkbox" checked={terms.send} disabled={!lead.lead.contact_email} onChange={(e) => setTerms({ ...terms, send: e.target.checked })} /> Email it to {lead.lead.contact_email ?? 'the contact (no email on file)'}
          </label>
          <Button variant="primary" disabled={!priced || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Saving…' : terms.send ? 'Send the quote' : 'Save the quote'}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function QuotesPanel({ quotes, leadId, canQuote, canBook, onError, refresh, notify }: { quotes: Quote[]; leadId: string; canQuote: boolean; canBook: boolean } & Report) {
  const [booking, setBooking] = useState<{ quote_id?: string; booking_id?: string; total: number } | null>(null);
  const send = useMutation({ mutationFn: (id: string) => groupBookingEndpoints.sendQuote(id), onError, onSuccess: (r) => { notify('Quote emailed again.', r.url); refresh(); } });
  const link = useMutation({ mutationFn: (id: string) => groupBookingEndpoints.quoteLink(id), onError, onSuccess: (r) => notify('The quote’s link:', r.url) });
  const hold = useMutation({ mutationFn: (id: string) => groupBookingEndpoints.hold(leadId, { quote_id: id, hold_tickets: true }), onError, onSuccess: (r) => { notify(`${r.booking.product_name ?? 'The space'} is held until ${formatWhen(r.booking.hold_expires_at)}, with its tickets set aside.`); refresh(); } });
  if (quotes.length === 0) return null;
  return (
    <Panel title="Quotes" description="Each version as it was sent. A newer one replaces the older.">
      <div className="divide-y divide-line">
        {quotes.map((quote) => {
          const current = ['DRAFT', 'SENT', 'ACCEPTED'].includes(quote.status);
          return (
            <div key={quote.id} className={`px-5 py-3 text-sm ${current ? '' : 'text-ink-400'}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">
                  Version {quote.version} · {quote.product_name ?? 'Tickets'} · {quote.event_title} · {formatWhen(quote.starts_at)}
                </span>
                <span className="flex items-center gap-2">
                  <Badge tone={quote.status === 'ACCEPTED' ? 'ok' : quote.status === 'SENT' ? 'accent' : 'neutral'}>{quote.status.toLowerCase()}</Badge>
                  <span className="tabular font-semibold">{dollars(quote.total_cents)}</span>
                </span>
              </div>
              <ul className="mt-1 text-xs text-ink-500">
                {quote.lines.map((line) => (
                  <li key={line.line_no}>
                    {line.description}: {dollars(line.amount_cents)}
                  </li>
                ))}
                {quote.deposit_cents > 0 ? <li>Deposit {dollars(quote.deposit_cents)} by {quote.deposit_due_on}</li> : null}
                {quote.sent_at ? <li>Sent {formatWhen(quote.sent_at)}{quote.viewed_at ? `, opened ${formatWhen(quote.viewed_at)}` : ''}</li> : null}
              </ul>
              {current ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {canQuote ? <Button onClick={() => send.mutate(quote.id)}>Email again</Button> : null}
                  {canQuote ? <Button onClick={() => link.mutate(quote.id)}>Copy link</Button> : null}
                  {canBook ? <Button onClick={() => hold.mutate(quote.id)}>Hold the space</Button> : null}
                  {canBook ? (
                    <Button variant="primary" onClick={() => setBooking({ quote_id: quote.id, total: quote.total_cents })}>
                      Book it
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {booking ? <BookForm leadId={leadId} target={booking} houseAccount={false} onClose={() => setBooking(null)} onError={onError} refresh={refresh} notify={notify} /> : null}
    </Panel>
  );
}

function BookingsPanel({ bookings, leadId, canBook, houseAccount, onError, refresh, notify }: { bookings: Booking[]; leadId: string; canBook: boolean; houseAccount: boolean } & Report) {
  const [booking, setBooking] = useState<{ booking_id: string; total: number } | null>(null);
  const [reason, setReason] = useState('');
  const release = useMutation({ mutationFn: (id: string) => groupBookingEndpoints.release(id, reason.trim() || 'Released by staff'), onError, onSuccess: () => refresh() });
  const cancel = useMutation({ mutationFn: (id: string) => groupBookingEndpoints.cancel(id, reason.trim() || 'Canceled by staff'), onError, onSuccess: () => refresh() });
  if (bookings.length === 0) return null;
  return (
    <Panel title="Holds and bookings">
      <Table headers={['Space', 'Game', 'Status', 'Order', 'Money', '']}>
        {bookings.map((b) => (
          <tr key={b.id} className="border-b border-line last:border-0">
            <td className="px-5 py-3 text-sm">
              {b.product_name}
              {b.option_names ? <div className="text-xs text-ink-500">+ {b.option_names}</div> : null}
              <div className="text-xs text-ink-500">{b.ticket_count} tickets</div>
            </td>
            <td className="px-5 py-3 text-sm">
              {b.event_title}
              <div className="text-xs text-ink-500">{formatWhen(b.starts_at)}</div>
            </td>
            <td className="px-5 py-3 text-sm">
              <Badge tone={BOOKING_TONE[b.status]}>{b.status.toLowerCase()}</Badge>
              {b.status === 'HELD' && b.hold_expires_at ? <div className="text-xs text-ink-500">until {formatWhen(b.hold_expires_at)}</div> : null}
              {b.close_reason ? <div className="text-xs text-ink-500">{b.close_reason}</div> : null}
            </td>
            <td className="px-5 py-3 text-sm">
              {b.order_id ? (
                <Link to={`/orders/${b.order_id}`} className="font-mono text-live hover:underline">
                  {b.order_number}
                </Link>
              ) : (
                '—'
              )}
              {b.group_hold_id ? (
                <div>
                  <Link to={`/group-holds/${b.group_hold_id}`} className="text-xs text-live hover:underline">
                    Deadline, link and invoice
                  </Link>
                </div>
              ) : null}
            </td>
            <td className="tabular px-5 py-3 text-sm">{b.total_cents !== null ? `${dollars(b.paid_cents ?? 0)} of ${dollars(b.total_cents)}` : '—'}</td>
            <td className="px-5 py-3 text-right text-sm">
              {canBook && b.status === 'HELD' ? (
                <div className="flex justify-end gap-2">
                  <Button variant="primary" onClick={() => setBooking({ booking_id: b.id, total: b.price_cents })}>
                    Book
                  </Button>
                  <Button variant="danger" onClick={() => release.mutate(b.id)}>
                    Release
                  </Button>
                </div>
              ) : null}
              {canBook && b.status === 'BOOKED' ? (
                <Button variant="danger" onClick={() => (window.confirm('Cancel this booking? An unpaid order is canceled and its seats go back on sale.') ? cancel.mutate(b.id) : undefined)}>
                  Cancel booking
                </Button>
              ) : null}
            </td>
          </tr>
        ))}
      </Table>
      {canBook ? (
        <div className="border-t border-line px-5 py-3">
          <Field label="Reason, for a release or cancel" htmlFor="b-reason">
            <input id="b-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={`w-80 ${INPUT}`} />
          </Field>
        </div>
      ) : null}
      {booking ? <BookForm leadId={leadId} target={booking} houseAccount={houseAccount} onClose={() => setBooking(null)} onError={onError} refresh={refresh} notify={notify} /> : null}
    </Panel>
  );
}

/** How it will be paid: a link (with an optional deposit), an invoice, or the organization's house account. */
function BookForm({ leadId, target, houseAccount, onClose, onError, refresh, notify }: { leadId: string; target: { quote_id?: string; booking_id?: string; total: number }; houseAccount: boolean; onClose: () => void } & Report) {
  const today = new Date().toISOString().slice(0, 10);
  const [payment, setPayment] = useState<'LINK' | 'INVOICE' | 'HOUSE_ACCOUNT'>('LINK');
  const [due, setDue] = useState(plus(today, 14));
  const [deposit, setDeposit] = useState('');
  const [depositDue, setDepositDue] = useState(plus(today, 7));
  const [sendLink, setSendLink] = useState(true);
  const [autoCancel, setAutoCancel] = useState(false);
  const [acceptPrice, setAcceptPrice] = useState(false);
  const book = useMutation({
    mutationFn: () =>
      groupBookingEndpoints.book(leadId, {
        ...(target.booking_id ? { booking_id: target.booking_id } : { quote_id: target.quote_id ?? null }),
        payment,
        balance_due_on: due,
        deposit_cents: payment === 'LINK' ? Math.round(Number(deposit || 0) * 100) : 0,
        deposit_due_on: payment === 'LINK' && deposit ? depositDue : null,
        send_link: sendLink,
        auto_cancel: autoCancel,
        accept_current_price: acceptPrice,
      }),
    onError,
    onSuccess: (r) => {
      notify(`Booked: order ${r.order_number}, ${dollars(r.total_cents)}.${r.payment_request ? ' Payment link:' : ''}`, r.payment_request?.url);
      onClose();
      refresh();
    },
  });
  return (
    <div className="space-y-3 border-t border-line bg-page px-5 py-4">
      <p className="text-sm font-medium">Book it: the space is taken, the tickets are sold into one order, and the contact gets a confirmation.</p>
      <div className="flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-1"><input type="radio" checked={payment === 'LINK'} onChange={() => setPayment('LINK')} /> Payment link (tickets when paid)</label>
        <label className="flex items-center gap-1"><input type="radio" checked={payment === 'INVOICE'} onChange={() => setPayment('INVOICE')} /> Invoice (tickets now)</label>
        <label className="flex items-center gap-1"><input type="radio" checked={payment === 'HOUSE_ACCOUNT'} disabled={!houseAccount} onChange={() => setPayment('HOUSE_ACCOUNT')} /> House account{houseAccount ? '' : ' (not set up)'}</label>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Everything due by" htmlFor="book-due">
          <input id="book-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} className={INPUT} />
        </Field>
        {payment === 'LINK' ? (
          <>
            <Field label="Deposit ($, optional)" htmlFor="book-deposit">
              <input id="book-deposit" inputMode="decimal" value={deposit} onChange={(e) => setDeposit(e.target.value.replace(/[^0-9.]/g, ''))} className={`w-28 ${INPUT}`} />
            </Field>
            {deposit ? (
              <Field label="Deposit due" htmlFor="book-deposit-due">
                <input id="book-deposit-due" type="date" value={depositDue} onChange={(e) => setDepositDue(e.target.value)} className={INPUT} />
              </Field>
            ) : null}
            <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={sendLink} onChange={(e) => setSendLink(e.target.checked)} /> Email the link now</label>
          </>
        ) : null}
        <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={autoCancel} onChange={(e) => setAutoCancel(e.target.checked)} /> Give it all back if not paid</label>
        <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={acceptPrice} onChange={(e) => setAcceptPrice(e.target.checked)} /> Book at today’s price if it changed</label>
      </div>
      <div className="flex gap-2">
        <Button variant="primary" disabled={book.isPending || !due} onClick={() => book.mutate()}>
          {book.isPending ? 'Booking…' : 'Book'}
        </Button>
        <Button onClick={onClose}>Not yet</Button>
      </div>
    </div>
  );
}
